/* eslint-disable rxjs/no-subscribe-handlers */
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BlobType } from '@azure/storage-blob';
import {
    CreateFolder,
    FileDeleteResponseDto,
    FileModel,
    ItemsToBeRemovedOptions,
    ListResponse,
    ListSearch,
    MEDFileAction,
    MEDFileColumnKeys,
    OptionItem,
    Result,
    SortOption,
} from '@ca/core/models';
import {
    DialogService,
    ExperimentalFeaturesService,
    FileDownloadService,
    FilePreviewService,
    FileService,
    ResultsService,
    UserNotificationService,
    UserService,
    UserWorkspaceService,
    WindowRef,
} from '@ca/core/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { FileColumn } from 'src/app/core/models/file-column.model';
import { ArivisService } from 'src/app/core/services/arivis.service';
import { FileExplorerMode } from 'src/app/core/types/file-explorer-mode.type';
import { FileSizePipe } from '../../../shared/pipes/file-size/file-size.pipe';
import { FILE_EXPLORER_MODE } from '../../constants/constants';

import { HttpParams } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, catchError, EMPTY, firstValueFrom, from, map, Observable, Subscription, switchMap, tap } from 'rxjs';
import { MEDResultDto } from 'src/app/core/models/med-result-dto.model';
import { MedResultItemVM } from 'src/app/core/models/med-result-item.model';
import { getUniqueUserIdsFromWorkflowSharesResults, getUserDetailsFromWorkflowShares } from 'src/utils/user/user-utils';
import { TableRow } from '../files-table/table-row.model';
import { ResultGroupShareWithUserModalComponent } from '../result-group-share-with-user-modal/result-group-share-with-user-modal.component';

export const ARCHIVED_FILES_FILENAME = 'my-files';
@UntilDestroy()
@Component({
    selector: 'ca-results-table',
    templateUrl: './results-table.component.html',
    styleUrls: ['./results-table.component.scss'],
})
export class ResultsTableComponent implements AfterViewInit, OnInit {
    @ViewChild('filePreview') filePreviewElement: ElementRef;

    @Input() mode: FileExplorerMode = FILE_EXPLORER_MODE.MODIFICATION;
    @Input() isLoading: boolean;
    @Input() isFileExplorerShown?: string;
    @Input() blobType: BlobType;
    @Input() folderId?: string;
    @Input() targetFolderToBeCreated?: CreateFolder;
    @Input() totalNumberOfFiles: number;
    @Input() set items(items) {
        this.numberOfFiles = 0;
        if (items) {
            this._rows = items.map((item: MedResultItemVM): { item: MedResultItemVM; selected: boolean; isFolder: boolean | undefined } => {
                const row = { item, selected: false, isFolder: item.isFolder };
                return row;
            });
            this.areAllFilesSelected = false;
            this.showShareColumn = items.every(i => i.isFolder);
            this.selectedRows = [];
            this.elementRef?.nativeElement?.scrollTo(0, 0);
        }
    }
    @Input() isAddAllFilesClickButtonShown = false;
    @Input() addAllFilesButtonTooltip = '';
    @Input() columns: FileColumn<MEDFileColumnKeys>[];
    @Input() actions: MEDFileAction[];
    @Input() totalRowCount: number;
    @Input() myFilesClass = false;
    @Input() stickyHeader = true;
    @Input() showFileStatus = false;
    @Input() truncateFileNameAtWidthOf?: number;
    @Input() allowSameFileNames = true;
    @Input() isFilesPage?: boolean = false;
    @Input() supportedFileTypes = new Array<string>();
    @Input() allFiles: MedResultItemVM[];
    @Input() showEmptyRows = true;
    @Input() showTableHead = true;

    @Output() deleteItems = new EventEmitter<ItemsToBeRemovedOptions<MedResultItemVM>>();
    @Output() onClickOnFile = new EventEmitter<MedResultItemVM>();
    @Output() onClickOnFolder = new EventEmitter<string>();
    @Output() onOpenFolder = new EventEmitter<string>();
    @Output() isFolderOpen = new EventEmitter<boolean>();

    result: Result;
    selectedRows: Array<TableRow> = [];
    tableMessages = {
        emptyMessage: this.translateService.instant('MY_WORKSPACE.MY_FILES.NO_FILES_FOUND'),
    };
    isLoading$: Observable<boolean>;
    isZipInProgress = false;
    isDownloadBusy = false;
    areAllFilesSelected = false;
    showShareColumn = false;
    fileTypeFilterOptions: string[] = [];
    totalItemsCount: number;
    numberOfFiles: number = 0;
    fileItemSubMenuOpenIndex: number | null = null;
    folderItemSubMenuOpenIndex: number | null = null;
    sortOptions: OptionItem<SortOption>[] = [
        { label: 'Sort by date desc', value: '-finishedAt' },
        { label: 'Sort by date asc', value: 'finishedAt' },
        { label: 'Sort by A-Z', value: 'title' },
        { label: 'Sort by Z-A', value: '-title' },
        { label: 'Sort by State', value: 'state' },
    ];
    sortByField = this.sortOptions[0]; // First sort option as default
    Array = Array;
    isMEDprojectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    private _rows: [];
    private downloadFilename = 'results';
    private readonly fileDownloadThresholdInBytes = 500 * 1024 * 1024;
    private searchSubscription?: Subscription;
    private isLoading$$ = new BehaviorSubject(false);
    readonly placeholderRowId = 'no-file-id';

    readonly defaultPageSize = 10;
    readonly defaultPageNumber = 1;
    readonly defaultSearchParams: ListSearch = {
        categoryIds: '',
        pageNumber: this.defaultPageNumber,
        pageSize: this.defaultPageSize,
        searchQuery: '',
        folderId: '',
        sortBy: this.sortByField.value,
    };
    currentSearchParams: ListSearch = this.defaultSearchParams;

    get rows(): TableRow<MedResultItemVM>[] {
        return this._rows;
    }

    get areSomeRowsSelected(): boolean {
        return this.selectedRows.length > 0;
    }

    get tableBannerText(): string {
        return this.getSelectedItemsText(this.selectedRows);
    }

    get selectedFiles(): MedResultItemVM[] {
        return this.getFiles(this.selectedRows);
    }

    get isLoggedIn(): boolean {
        return this.userService.isLoggedIn;
    }

    get isMine(): boolean {
        let isMine = false;
        if (this.userService.myInfo && this.userService.userSub) {
            isMine = this.userService.userSub === this.result.userId;
        }
        return isMine;
    }

    constructor(
        private arivisService: ArivisService,
        private elementRef: ElementRef,
        private filePreviewService: FilePreviewService,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private resultsService: ResultsService,
        private windowRef: WindowRef,
        private fileService: FileService,
        private activatedRoute: ActivatedRoute,
        private userService: UserService,
        private fileDownloadService: FileDownloadService,
        private fileSizePipe: FileSizePipe,
        private dialogService: DialogService,
        private modalService: NgbModal,
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private userWorkspaceService: UserWorkspaceService,
    ) {}

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe({
            next: (params: { [key: string]: string }): void => {
                const folderId = params['folderId'] || '';
                const searchQuery = params['q'] || '';
                const sortBy = (params['sortBy'] as SortOption) || this.sortOptions[0].value;
                this.currentSearchParams.searchQuery = searchQuery;
                this.currentSearchParams.sortBy = sortBy;
                this.currentSearchParams.folderId = folderId;
                this.currentSearchParams.pageNumber = Number(params['page[number]']) || this.defaultPageNumber;
                this.currentSearchParams.pageSize = Number(params['page[size]']) || this.defaultPageSize;
                this.isFolderOpen.emit(!!folderId);
            },
            error: (err: unknown): void => {
                console.error('Failed to extract query params:', err);
            },
            complete: (): void => {
                console.log('Query param subscription completed.');
            },
        });
    }

    /**
     * Ensures checkbox column presence.
     */
    ngAfterViewInit(): void {
        this.isCheckboxIncluded();
    }

    /**
     * Checks if at least one selected file is previewable by Arivis.
     * @returns {boolean} True if at least one file is previewable, otherwise false.
     */
    isAtLeastOneArivisPreviewableFileSelected(): boolean {
        return this.filePreviewService.isAtLeastOneArivisPreviewableFileSelected(this.selectedFiles);
    }

    /**
     * Toggles the selection of all rows in the table.
     */
    onSelectAllRowsChange(): void {
        this.areAllFilesSelected = !this.areAllFilesSelected;
        this.selectedRows = this.areAllFilesSelected ? [...this.rows] : [];
        this.rows.forEach((f: TableRow): void => {
            f.selected = this.areAllFilesSelected;
        });
    }

    /**
     * Determines if a given item belongs to the current user.
     * @param {MedResultItemVM} item - The item to check.
     * @returns {boolean} True if the item belongs to the current user, otherwise false.
     */
    isItMY(item: MedResultItemVM): boolean {
        return item.userId != null ? this.userService.isCurrentUser(item.userId) : false;
    }

    /**
     * Opens selected files in the Arivis ML Viewer.
     */
    openSelectedFilesInArivis(): void {
        const previewableFiles = this.selectedFiles.filter((file: MedResultItemVM): boolean => this.arivisService.isSupportedByArivis(file.type));
        const url: string = this.arivisService.generateArivisMlViewerUrl(previewableFiles);

        this.windowRef.nativeWindow.open(url, '_blank');
    }

    /**
     * Handles changes to the selection state of a row's checkbox.
     * @param {TableRow} row - The row for which the checkbox state changed.
     */
    onRowCheckboxChange(row: TableRow): void {
        row.selected = !row.selected;

        if (row.selected) {
            this.selectedRows.push(row);
        } else {
            this.selectedRows = this.selectedRows.filter(({ item }: TableRow): boolean => item.id !== row.item.id);
        }
        this.setAllFilesSelected();
    }

    /**
     * Opens the sharing modal for a specific item.
     * @param {MedResultItemVM} item - The item to share.
     */
    onSharing(item: MedResultItemVM): void {
        const modal = this.modalService.open(ResultGroupShareWithUserModalComponent, { size: 'lg', centered: true });
        const modalComponentInstance = <ResultGroupShareWithUserModalComponent>modal.componentInstance;

        const clonedUsers = JSON.parse(JSON.stringify(item != null && item.shares != null && item.shares.length > 0 ? item.shares[0].users : []));
        modalComponentInstance.share = {
            id: item.id,
            title: item.title,
            users: clonedUsers,
        };
        modal.result.then(
            result => {
                if (Array.isArray(result)) {
                    this.searchResult();
                }
            },
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            () => {},
        );
    }

    /**
     * Handles a click event on a table row, including checkbox selection or row-specific actions.
     * @param {TableRow} row - The row that was clicked.
     * @param {Event} event - The click event.
     */
    handleRowClick(row: TableRow, event: Event): void {
        if (this.isCheckboxIncluded() && event.type === 'click') {
            this.onRowCheckboxChange(row);
        }
        if (event.type === 'click') {
            if (row.isFolder) {
                this.onClickOnFolder.emit(row.item.id);
            } else {
                this.onClickOnFile.emit(<MedResultItemVM>row.item);
            }
        }
    }

    /**
     * Retrieves the default value for a specified column.
     * @param {string} columnName - The name of the column.
     * @returns {string} The default value for the column or '--' if not defined.
     */
    getDefaultColumnValue(columnName: string): string {
        const column = this.columns.find(column => column.key === columnName);
        if (column && column.defaultValue) {
            return column.defaultValue;
        } else {
            return '--';
        }
    }

    /**
     * Determines the CSS classes for a specific table row.
     * @param {any} _row - The table row object.
     * @param {number} index - The index of the row.
     * @param {boolean} first - Indicates if the row is the first in a group.
     * @returns {{ 'group-child-row-first-in-group': boolean; 'is-submenu-open': boolean }} Object containing CSS class conditions.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    getRowClasses(index: number, first: boolean, _row: any = []): { 'group-child-row-first-in-group': boolean; 'is-submenu-open': boolean } {
        return {
            'group-child-row-first-in-group': first,
            'is-submenu-open': this.fileItemSubMenuOpenIndex === index || this.folderItemSubMenuOpenIndex === index,
        };
    }

    /**
     * Handles opening and closing of the file item submenu.
     * @param {number} index - The index of the file item in the list.
     * @param {boolean} isOpen - Whether the submenu is open or closed.
     */
    onFileItemSubMenuOpen(index: number, isOpen: boolean): void {
        if (isOpen) {
            this.fileItemSubMenuOpenIndex = index;
        } else if (this.fileItemSubMenuOpenIndex === index) {
            this.fileItemSubMenuOpenIndex = null;
        }
    }

    /**
     * Handles deletion of items from a child component, triggering the deletion process.
     * @param {{ items: MedResultItemVM[]; shouldConfirmDialogBeOpened?: boolean }} event - The event object containing items to delete and confirmation flag.
     */
    onDeleteItemsFromChild(event: { items: MedResultItemVM[]; shouldConfirmDialogBeOpened?: boolean }): void {
        const itemsToDelete = event.items.map(item => ({
            isFolder: item.isFolder,
            item,
        }));
        const foldersToDelete: string[] = itemsToDelete.filter(row => row.isFolder).map(row => (row.item as MedResultItemVM).id);
        const filesToDelete: string[] = itemsToDelete.filter(row => !row.isFolder).map(row => (row.item as MedResultItemVM).id);
        const userId: string | undefined = itemsToDelete.filter(row => row.isFolder).map(row => (row.item as MedResultItemVM).userId)[0];

        this.handleDeletion('folder', foldersToDelete, userId);
        this.handleDeletion('file', filesToDelete);
    }

    /**
     * Handles opening and closing of the folder item submenu.
     * @param {number} index - The index of the folder item in the list.
     * @param {boolean} isOpen - Whether the submenu is open or closed.
     */
    onFolderItemSubMenuOpen(index: number, isOpen: boolean): void {
        if (isOpen) {
            this.folderItemSubMenuOpenIndex = index;
        } else if (this.folderItemSubMenuOpenIndex === index) {
            this.folderItemSubMenuOpenIndex = null;
        }
    }

    /**
     * Checks if a checkbox column is included in the table.
     * @returns {boolean} True if the checkbox column exists, otherwise false.
     */
    isCheckboxIncluded(): boolean {
        return this.columns?.some(column => column.key === 'checkbox');
    }

    /**
     * Checks if a file status column is included in the table.
     * @returns {boolean} True if the file status column exists, otherwise false.
     */
    isFileStatusIncluded(): boolean {
        return this.columns?.some(column => column.key === 'fileStatus');
    }

    /**
     * Checks if the file explorer is empty and in modification mode.
     * @returns {boolean} True if there are no rows, the mode is modification, and the explorer is not loading.
     */
    isEmptyAndModificationMode(): boolean {
        return !this.isLoading && this.mode === FILE_EXPLORER_MODE.MODIFICATION && this.rows?.length === 0;
    }

    /**
     * Checks if the given row represents a file.
     * @param {TableRow<MedResultItemVM>} row - The table row to check.
     * @returns {boolean} True if the row represents a file, false if it represents a folder.
     */
    isRowFile(row: TableRow<MedResultItemVM>): boolean {
        return !row.isFolder;
    }

    /**
     * Determines if the preview button should be enabled for the specified item.
     * @param {MedResultItemVM} item - The item to check.
     * @returns {boolean} True if the item is supported by Arivis or the file previewer and the 'view' action is allowed.
     */
    isPreviewBtnEnabled(item: MedResultItemVM): boolean {
        return this.actions.includes('view') && (this.arivisService.isSupportedByArivis(item.type) || this.arivisService.isSupportedByFilePreviewer(item.type));
    }

    /**
     * Determines if the download button should be enabled based on the selected rows.
     * @returns {boolean} True if the 'download' action is allowed and no folders are selected.
     */
    isDownloadBtnEnabled(): boolean {
        return this.actions.includes('download') && this.selectedRows.findIndex(x => x.isFolder === true) === -1;
    }

    /**
     * Determines if the delete button should be enabled.
     * @returns {boolean} True if the 'delete' action is allowed and the file explorer is in modification mode.
     */
    isDeleteBtnEnabled(): boolean {
        return this.actions.includes('delete') && this.mode === FILE_EXPLORER_MODE.MODIFICATION;
    }

    /**
     * Initiates the deletion process for the selected rows, including confirmation dialogs.
     */
    onDeleteSelectedResults(): void {
        const foldersToDelete: string[] = this.selectedRows.filter(row => row.isFolder).map(row => (row.item as MedResultItemVM).id);
        const filesToDelete: string[] = this.selectedRows.filter(row => !row.isFolder).map(row => (row.item as MedResultItemVM).id);
        const userId: string | undefined = this.selectedRows.filter(row => row.isFolder).map(row => (row.item as MedResultItemVM).userId)[0];

        this.handleDeletion('folder', foldersToDelete, userId);
        this.handleDeletion('file', filesToDelete);
    }

    /**
     * Handles the confirmation and deletion process for the specified type of items.
     * @param {'folder' | 'file'} deletionType - The type of items to delete ('folder' or 'file').
     * @param {string[]} itemsToDelete - Array of item IDs to delete.
     * @param {string} [userId] - The user ID required for folder deletion.
     */
    handleDeletion = (deletionType: 'folder' | 'file', itemsToDelete: string[], userId?: string): void => {
        if (itemsToDelete.length > 0) {
            const confirmModal = this.dialogService.openConfirm({
                message:
                    deletionType === 'folder'
                        ? this.translateService.instant('MY_WORKSPACE.MY_RESULTS.DELETE_RESULT')
                        : this.translateService.instant('MY_WORKSPACE.MY_RESULTS.DELETE_RESULT_FILES'),
                confirmText: this.translateService.instant('GENERAL.DELETE'),
            });

            confirmModal.result
                .then((): void => {
                    this.userNotificationService.showSpinner();
                    if (deletionType === 'folder' && userId) {
                        this.deleteFolders(itemsToDelete, userId);
                    } else if (deletionType === 'file') {
                        this.deleteFiles(itemsToDelete);
                    } else {
                        console.error(`User ID is missing for folder deletion.`);
                    }
                })
                .catch((): void => {
                    console.log(`${deletionType.charAt(0).toUpperCase() + deletionType.slice(1)} deletion cancelled by user.`);
                });
        }
    };

    /**
     * Downloads the selected files. If the total size exceeds a specified threshold,
     * a confirmation dialog is displayed before downloading.
     */
    downloadSelectedFiles(): void {
        const totalSize = this.getTotalSelectedFilesSize();

        if (totalSize >= this.fileDownloadThresholdInBytes) {
            this.showDownloadConfirmationDialog(totalSize)
                .then((): void => this.executeFileDownload())
                .catch((): void => {
                    // User canceled the confirmation dialog - do nothing;
                });
        } else {
            this.executeFileDownload();
        }
    }

    /**
     * Calculates the total size of the selected files.
     * @returns {number} The total size in bytes.
     */
    private getTotalSelectedFilesSize(): number {
        return this.selectedRows.reduce((total, row): number => total + (row.item as FileModel).sizeInBytes, 0);
    }

    /**
     * Displays a confirmation dialog for large downloads.
     * @param {number} totalSize - The total size of the files to download.
     * @returns {Promise<void>} A promise resolved when the user confirms the download.
     */
    private showDownloadConfirmationDialog(totalSize: number): Promise<void> {
        const fileSize = this.fileSizePipe.transform(totalSize);
        const confirmModal = this.dialogService.openConfirm({
            message: this.translateService.instant('MY_WORKSPACE.MY_RESULTS.DOWNLOAD_SELECTED_FILES', { fileSize }),
            confirmText: this.translateService.instant('GENERAL.DOWNLOAD'),
        });
        return confirmModal.result;
    }

    /**
     * Executes the file download based on the selected rows.
     */
    private executeFileDownload(): void {
        if (this.selectedRows.length === 1) {
            const file = this.selectedRows[0].item as FileModel;
            this.fileDownloadService.nativeDownload('', file.name, file.id);
        } else {
            const fileIds = this.selectedRows.map((row: TableRow): string => row.item.id);
            this.fileDownloadService.nativeDownloadFromStream(fileIds, this.downloadFilename);
        }
    }

    /**
     * Updates the `areAllFilesSelected` flag to indicate if all files in the table are selected.
     */
    private setAllFilesSelected(): void {
        this.areAllFilesSelected = this.selectedRows.length === this._rows?.length;
    }

    /**
     * Generates a notification text summarizing the count of selected files and folders.
     * @param {TableRow[]} selectedRows - Array of selected rows containing files and folders.
     * @returns {string} A localized string describing the selected items.
     */
    private getSelectedItemsText(selectedRows: TableRow[]): string {
        const filesCount = selectedRows?.filter(i => !i.isFolder).length || 0;
        const foldersCount = selectedRows?.filter(i => i.isFolder).length || 0;

        if (foldersCount === 1 && filesCount === 1) {
            return this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDER_AND_FILE_SELECTED');
        }

        if (foldersCount === 1 && filesCount > 1) {
            return this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDER_AND_FILES_SELECTED', { filesCount });
        }

        if (foldersCount > 1 && filesCount === 1) {
            return this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDERS_AND_FILE_SELECTED', { foldersCount });
        }

        if (foldersCount > 1 && filesCount > 1) {
            return this.translateService.instant('MY_WORKSPACE.MY_FILES.FOLDERS_AND_FILES_SELECTED', { foldersCount, filesCount });
        }

        if (foldersCount > 0) {
            return this.translateService.instant(foldersCount > 1 ? 'MY_WORKSPACE.MY_FILES.FOLDERS_SELECTED' : 'MY_WORKSPACE.MY_FILES.FOLDER_SELECTED', {
                foldersCount,
            });
        }

        if (filesCount > 0) {
            return this.translateService.instant(filesCount > 1 ? 'MY_WORKSPACE.MY_FILES.FILES_SELECTED' : 'MY_WORKSPACE.MY_FILES.FILE_SELECTED', {
                filesCount,
            });
        }

        return '';
    }

    /**
     * Filters out folder items from the provided list and maps them to `MedResultItemVM` objects.
     * @param {TableRow[]} items - Array of table rows to process.
     * @returns {MedResultItemVM[]} Array of `MedResultItemVM` representing non-folder items.
     */
    private getFiles(items: TableRow[]): MedResultItemVM[] {
        return items.filter(i => !i.isFolder).map(i => <MedResultItemVM>i.item);
    }

    /**
     * Performs a search for MED results based on the current search parameters.
     * Fetches data, processes it, and updates the result items and related states.
     * Cancels any ongoing search if applicable.
     */
    private searchResult(): void {
        const searchParams = this.currentSearchParams;
        const params = new HttpParams()
            .set('page[number]', searchParams.pageNumber.toString())
            .set('page[size]', searchParams.pageSize.toString())
            .set('sort', searchParams.sortBy)
            .set('title', searchParams.searchQuery)
            .set('text', searchParams.searchQuery)
            .set('groupId', searchParams.folderId ?? '');

        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        this.isLoading$$.next(true);
        this.searchSubscription = this.resultsService
            .searchGroupMED$(params)
            .pipe(
                tap((res: ListResponse<MEDResultDto>): void => {
                    this.totalRowCount = res?.meta?.totalCount ?? 0;
                }),
                map((res: ListResponse<MEDResultDto>): MEDResultDto[] => res.data),
                switchMap(
                    (res: MEDResultDto[]): Observable<void> =>
                        from(
                            (async (): Promise<void> => {
                                const allUniqueSharedToUserIds = getUniqueUserIdsFromWorkflowSharesResults(res);
                                const userInfos = await firstValueFrom(this.userService.getUserInfos$(Array.from(allUniqueSharedToUserIds)));
                                this.items = res.map((r: MEDResultDto): MedResultItemVM => {
                                    return {
                                        id: r.id,
                                        title: r.title,
                                        date: r.date,
                                        isFolder: r.isFolder,
                                        workflowResultTitle: r.workflowResultTitle,
                                        applicationTitle: r.applicationTitle,
                                        type: r.type,
                                        fileSize: r.fileSize,
                                        userId: r.userId,
                                        shares:
                                            r.workflowResultShares != null
                                                ? [
                                                      {
                                                          id: '',
                                                          title: r.title,
                                                          users: getUserDetailsFromWorkflowShares(r.workflowResultShares, userInfos),
                                                      },
                                                  ]
                                                : [],
                                    };
                                });
                                this.areAllFilesSelected = false;
                                this.selectedRows = [];
                                this.isLoading$$.next(false);
                            })(),
                        ),
                ),
                catchError((err: unknown): Observable<never> => {
                    console.error('Failed loading my results', err);
                    this.areAllFilesSelected = false;
                    this.selectedRows = [];
                    this.isLoading$$.next(false);
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    /**
     * Deletes the specified folders for a user and updates the workspace state.
     * @param {string[]} foldersToDelete - Array of folder IDs to be deleted.
     * @param {string} userId - ID of the user performing the delete operation.
     */
    private deleteFolders(foldersToDelete: string[], userId: string): void {
        this.resultsService
            .deleteSelectedResults$([], undefined, true, foldersToDelete, userId)
            .pipe(
                tap((): void => {
                    this.searchResult();
                }),
                switchMap((): Observable<void> => this.userWorkspaceService.updateMyResults$()),
                tap((): void => {
                    this.areAllFilesSelected = false;
                    this.selectedRows = [];
                    this.userNotificationService.hideSpinner();
                }),
                catchError((err: unknown): Observable<never> => {
                    console.error('Error deleting folders:', err);
                    this.userNotificationService.hideSpinner();
                    return EMPTY;
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    /**
     * Deletes the specified files and updates the workspace state.
     * @param {string[]} filesToDelete - Array of file IDs to be deleted.
     */
    private deleteFiles(filesToDelete: string[]): void {
        this.fileService
            .deleteFiles$(filesToDelete)
            .pipe(
                tap((resp: FileDeleteResponseDto[]): void => {
                    this.fileService.checkFilesDeletionStatus(filesToDelete, resp); // Notification system
                    this.searchResult();
                }),
                switchMap((): Observable<void> => this.userWorkspaceService.updateMyFiles$()),
                tap((): void => {
                    this.areAllFilesSelected = false;
                    this.selectedRows = [];
                    this.userNotificationService.hideSpinner();
                }),
                catchError((err: unknown): Observable<never> => {
                    console.error('Failed deleting selected files:', err);
                    this.userNotificationService.hideSpinner();
                    return EMPTY;
                }),
            )
            // eslint-disable-next-line rxjs/no-subscribe-handlers
            .subscribe({
                next: (): void => {
                    console.log('Files deleted successfully!');
                },
                error: (err: unknown): void => {
                    console.error('Error occurred during file deletion:', err);
                },
                complete: (): void => {
                    console.log('File deletion process completed.');
                },
            });
    }
}
